import { ClauseNode, TextNode } from './form/fill-out-llm-prompt'
import {
  Icon,
  EmojiSmilingIcon,
  GGOActionMakeItProfessionalIcon,
  GGOActionRewriteCategoryIcon,
} from '@grammarly/design-system'

export type SideBySideClickState = 'notClicked' | 'clicked'

const textClause: TextNode = {
  kind: 'text',
  value: 'TEXT:\n\n',
}

const getInputClauseWithVarName = (varName: string): ClauseNode => {
  return {
    content: [{ kind: 'var', varName }],
    kind: 'clause',
    varName,
  }
}

// Copied from existing paraphrasing tool prompt (2024-10-30)
const standardInstructionClause: TextNode = {
  kind: 'text',
  value:
    '\n\n\n\nTake the TEXT above, and reword each sentence. Do not remove any of the meaning, and do not add new sentences.\nREWORDED_TEXT:\n',
}

// Based on https://gitlab.grammarly.io/nlp-research/cheetah/composer/-/blob/main/simbaroot/simbalang/res/actions/Rewrite/Formality/Formal.pep (2024-10-30)
const professionalInstructionClause: TextNode = {
  kind: 'text',
  value:
    '\n\n\n\nTake the TEXT above so it sounds professional and respectful. It should sound appropriate to share with a boss or external audience. It should not use slang, casual words, or overly familiar or unprofessional language. Do not mention the boss in the text. Do not remove any of the meaning, and do not add new sentences.\nREWORDED_TEXT:\n',
}

// Based on https://gitlab.grammarly.io/nlp-research/cheetah/composer/-/blob/main/simbaroot/simbalang/res/actions/VBar/G2/Friendly.pep (2024-10-30)
const friendlyInstructionClause: TextNode = {
  kind: 'text',
  value:
    '\n\n\n\nTake the TEXT above, and reword each sentence, giving it a friendlier touch that increases warmth and makes it more welcoming. Adjust the language, sentence structure, and choice of words to make the text feel more personal and engaging to readers, all while keeping true to the original message, and do not add new sentences.\nREWORDED_TEXT:\n',
}

export const ParaphrasingPromptTypes = ['standard', 'professional', 'friendly'] as const
export type ParaphrasingPromptType = (typeof ParaphrasingPromptTypes)[number]
export const ParaphrasingPromptTypesToData: {
  [key in ParaphrasingPromptType]: {
    radioLabel: string
    radioIcon: React.ReactNode
    outputLabel: string
    getClauses: (varName: string) => (TextNode | ClauseNode)[]
  }
} = {
  standard: {
    radioLabel: 'Standard',
    radioIcon: <Icon icon={GGOActionRewriteCategoryIcon} accessibilityLabel="Meaning of icon" />,
    outputLabel: 'Improved text',
    getClauses: (varName) => [textClause, getInputClauseWithVarName(varName), standardInstructionClause],
  },
  professional: {
    radioLabel: 'Professional',
    radioIcon: <Icon icon={GGOActionMakeItProfessionalIcon} accessibilityLabel="Meaning of icon" />,
    outputLabel: 'Professional text',
    getClauses: (varName) => [textClause, getInputClauseWithVarName(varName), professionalInstructionClause],
  },
  friendly: {
    radioLabel: 'Friendly',
    radioIcon: <Icon icon={EmojiSmilingIcon} accessibilityLabel="Meaning of icon" />,
    outputLabel: 'Friendly text',
    getClauses: (varName) => [textClause, getInputClauseWithVarName(varName), friendlyInstructionClause],
  },
} as const
